/**
 * storyProgress.js — client-side reading progress for the Stories Hub.
 * Per active Explorer profile, localStorage only (no accounts, no server
 * writes). Feeds "Continue reading" on /stories and the episode dots.
 */
import { getActiveChildId } from "@/lib/children";
import { getSeries } from "@/data/stories";

const KEY = "mwq_story_progress_v1";
const BAND_KEY = "mwq_story_band_v1";

function learnerId() {
  try { return getActiveChildId() || "local"; } catch { return "local"; }
}
function readAll() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch { return {}; }
}
function writeAll(o) {
  try { localStorage.setItem(KEY, JSON.stringify(o)); } catch { /* ignore */ }
}
function bucket() {
  const all = readAll();
  const lid = learnerId();
  return all[lid] || { current: null, completed: {} };
}
function saveBucket(b) {
  const all = readAll();
  all[learnerId()] = b;
  writeAll(all);
  try { window.dispatchEvent(new CustomEvent("mwq-story-progress-changed")); } catch { /* ignore */ }
}

// ── Band preference (per learner) ───────────────────────────────────
export function getStoryBand() {
  try {
    const all = JSON.parse(localStorage.getItem(BAND_KEY) || "{}");
    return all[learnerId()] || null;
  } catch { return null; }
}
export function setStoryBand(band) {
  try {
    const all = JSON.parse(localStorage.getItem(BAND_KEY) || "{}");
    all[learnerId()] = band;
    localStorage.setItem(BAND_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

// ── Reading pointer ─────────────────────────────────────────────────
export function setStoryProgress({ seriesSlug, episodeSlug, pageIndex, band }) {
  const b = bucket();
  b.current = { seriesSlug, episodeSlug, pageIndex: pageIndex || 0, band: band || "explorers" };
  saveBucket(b);
}

export function getContinueReading() {
  const b = bucket();
  return b.current || null;
}

export function markEpisodeComplete(seriesSlug, episodeSlug) {
  const b = bucket();
  b.completed = b.completed || {};
  const list = new Set(b.completed[seriesSlug] || []);
  list.add(episodeSlug);
  b.completed[seriesSlug] = [...list];
  saveBucket(b);
}

export function completedEpisodes(seriesSlug) {
  const b = bucket();
  return (b.completed && b.completed[seriesSlug]) || [];
}

export function isEpisodeComplete(seriesSlug, episodeSlug) {
  return completedEpisodes(seriesSlug).includes(episodeSlug);
}

/** First unread episode slug for a series, else episode 1. */
export function firstUnreadEpisode(seriesSlug) {
  const s = getSeries(seriesSlug);
  if (!s || !s.episodes.length) return null;
  const done = new Set(completedEpisodes(seriesSlug));
  const unread = s.episodes.find((e) => !done.has(e.slug));
  return (unread || s.episodes[0]).slug;
}
